import Seo, { articleSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Flame,
  Pill,
  ArrowRight,
  GraduationCap,
  ShieldAlert,
  Eye,
  Sparkles,
} from "lucide-react";

const NAMES = [
  "Pasta base",
  "Angustia",
  "Mono",
  "Paco",
  "Bazuco",
  "Pasta",
  "Base",
  "PBC",
];

const COMP = [
  { drug: "Hojas de coca", purity: "1–2%", state: "Hoja natural masticable" },
  { drug: "Pasta base", purity: "40–60% con solventes", state: "Pasta impura fumable" },
  { drug: "Cocaína (clorhidrato)", purity: "50–90% (cortada 20–40%)", state: "Polvo cristalino aspirable" },
  { drug: "Crack", purity: "70–90%", state: "Cristales fumables" },
];

// --- Bloque de efectos a corto plazo (tabla) ---
const EFECTOS_CORTO = [
  { area: "Estado de ánimo", efectos: "Euforia breve, irritabilidad, ansiedad, angustia, cambios bruscos de ánimo" },
  { area: "Conducta", efectos: "Impulsividad, desinhibición, agresividad, búsqueda urgente de consumo" },
  { area: "Sueño y energía", efectos: "Insomnio, hiperactividad inicial, agotamiento posterior" },
  { area: "Cognición", efectos: "Dificultad para concentrarse, pensamientos acelerados, desorganización" },
  { area: "Cuerpo", efectos: "Palpitaciones, sudoración, temblores, pérdida de apetito, deterioro respiratorio si el consumo se mantiene" },
  { area: "Vida cotidiana", efectos: "Ausentismo escolar o laboral, conflictos familiares, endeudamiento, exposición a entornos de riesgo" },
];

// --- Estadísticas oficiales (con fuentes) ---
const STATS_OFICIALES = [
  {
    indicador: "Consumo en población general (último año)",
    valor: "0,3%",
    lectura: "Personas entre 12 y 64 años — 15° ENPG.",
    sources: ["senda-pbc"],
  },
  {
    indicador: "Consumo en población escolar (último año)",
    valor: "1,8%",
    lectura: "Estudiantes de 8° básico a 4° medio — 15° Estudio Escolar.",
    sources: ["senda-pbc"],
  },
  {
    indicador: "Tendencia desde 2010",
    valor: "Estable",
    lectura: "El consumo en último año en población general no varía significativamente desde 2010.",
    sources: ["senda-resultados"],
  },
  {
    indicador: "Percepción de riesgo (uso experimental)",
    valor: "87,3%",
    lectura: "Población general que percibe riesgo en el uso experimental de pasta base.",
    sources: ["senda-resultados"],
  },
  {
    indicador: "Consumo problemático entre consumidores",
    valor: "54,8%",
    lectura: "Proporción que cumplía criterios de consumo problemático (datos 2016 citados por MINSAL).",
    sources: ["minsal-diprece"],
  },
];

// --- Señales de alerta (tabla) ---
const SENALES = [
  { tipo: "Cambios físicos", ejemplos: "Pérdida de peso, ojeras, descuido personal, tos persistente, cansancio extremo" },
  { tipo: "Cambios emocionales", ejemplos: "Irritabilidad, angustia, agresividad, ansiedad, episodios de paranoia" },
  { tipo: "Cambios conductuales", ejemplos: "Desapariciones, mentiras frecuentes, venta de objetos, endeudamiento, abandono de rutinas" },
  { tipo: "Cambios sociales", ejemplos: "Nuevas amistades de alto riesgo, aislamiento familiar, conflictos con vecinos o compañeros" },
  { tipo: "Cambios escolares / laborales", ejemplos: "Ausencias, bajo rendimiento, pérdida de motivación, expulsiones o despidos" },
  { tipo: "Señales de dependencia", ejemplos: "Deseo intenso, dificultad para detenerse, aumento de cantidad, consumo pese a consecuencias" },
];

// --- Mitos vs realidades (tabla) ---
const MITOS = [
  {
    mito: "“Si la prueba una vez, siempre se vuelve adicto”",
    realidad: "No toda prueba produce dependencia inmediata, pero la pasta base tiene alto riesgo de escalada por su efecto rápido y breve.",
  },
  {
    mito: "“Solo consume quien quiere destruirse”",
    realidad: "El consumo problemático se relaciona con salud mental, trauma, contexto social, disponibilidad, pares y vulnerabilidad.",
  },
  {
    mito: "“Hay que esperar a que la persona pida ayuda sola”",
    realidad: "La intervención temprana puede reducir daño y mejorar el acceso a tratamiento.",
  },
  {
    mito: "“La familia no puede hacer nada”",
    realidad: "La familia puede ser un factor protector si busca orientación, establece límites y participa en el proceso terapéutico.",
  },
  {
    mito: "“Las estadísticas bajas significan que no es un problema importante”",
    realidad: "La prevalencia puede ser baja en población general, pero el daño entre consumidores frecuentes puede ser muy alto.",
  },
];

// --- Fuentes oficiales citadas ---
const SOURCES = {
  "senda-pbc": {
    label: "SENDA — Información droga: pasta base",
    href: "https://www.senda.gob.cl/informacion-droga/pasta-base/",
  },
  "senda-resultados": {
    label: "SENDA — Resultados 15° Estudio en Población General",
    href: "https://www.senda.gob.cl/senda-presenta-principales-resultados-del-15-estudio-de-drogas-en-poblacion-general/",
  },
  "senda-escolar": {
    label: "SENDA — 15° Estudio Nacional de Drogas en Población Escolar",
    href: "https://www.senda.gob.cl/noticia/senda-pone-a-disposicion-el-informe-final-del-15-estudio-nacional-de-drogas-en-poblacion-escolar/",
  },
  "senda-observatorio": {
    label: "Observatorio Chileno de Drogas — SENDA",
    href: "https://www.senda.gob.cl/informacion-y-conocimiento/observatorio-chileno-drogas/",
  },
  "senda-1412": {
    label: "SENDA — Fono Drogas 1412",
    href: "https://www.senda.gob.cl/noticia/en-los-lagos-lanzan-segunda-temporada-de-la-audioserie-1412-esto-es-sin-juzgar/",
  },
  "senda-parentalidad": {
    label: "SENDA — Herramientas para padres",
    href: "https://www.senda.gob.cl/prevencion/iniciativas/herramientas-para-padres/",
  },
  "minsal-diprece": {
    label: "MINSAL DIPRECE — Descripción y epidemiología (consumo en menores de 20 años)",
    href: "https://diprece.minsal.cl/garantias-explicitas-en-salud-auge-o-ges/guias-de-practica-clinica/consumo-perjudicial-y-dependencia-de-alcohol-y-otras-drogas-en-personas-menores-de-20-anos/descripcion-y-epidemiologia/",
  },
  "minsal-recomendaciones": {
    label: "MINSAL DIPRECE — Recomendaciones clínicas",
    href: "https://diprece.minsal.cl/garantias-explicitas-en-salud-auge-o-ges/guias-de-practica-clinica/consumo-perjudicial-y-dependencia-de-alcohol-y-otras-drogas-en-personas-menores-de-20-anos/recomendaciones/",
  },
  "conaset": {
    label: "CONASET — Clasificación de drogas y efectos en el tránsito",
    href: "https://www.conaset.cl/alcohol-y-conduccion/clasificacion-de-las-drogas-y-sus-efectos-en-el-transito/",
  },
  "aduanas-chacalluta": {
    label: "Aduanas — Incautación en Chacalluta (abril 2024)",
    href: "https://www.aduana.cl/efectiva-labor-de-aduanas-en-chacalluta-incautan-mas-de-48-kilos-de-drogas/aduana/2024-04-15/163033.html",
  },
  "aduanas-elloa": {
    label: "Aduanas — Incautación en El Loa (agosto 2024)",
    href: "https://www.aduana.cl/camion-escaner-intercepta-contrabando-de-23-kilos-de-marihuana-y-9-kilos/aduana/2024-08-14/104959.html",
  },
};

function Cite({ ids }) {
  return (
    <span className="ml-0.5 inline-flex gap-0.5 align-super text-[11px] leading-none">
      {ids.map((id) => {
        const s = SOURCES[id];
        if (!s) return null;
        const num = Object.keys(SOURCES).indexOf(id) + 1;
        return (
          <a
            key={id}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            title={s.label}
            className="rounded bg-emerald-500/15 px-1 py-0.5 font-semibold text-emerald-300 no-underline hover:bg-emerald-500/25"
          >
            [{num}]
          </a>
        );
      })}
    </span>
  );
}

// --- FAQ propio de este artículo (con citas) ---
const ARTICLE_FAQ = [
  {
    question: "¿Qué es la pasta base?",
    answer:
      "La pasta base es un derivado intermedio en la producción de cocaína que contiene alcaloides de coca y solventes, y generalmente se consume fumada (SENDA).",
  },
  {
    question: "¿Cuáles son los principales efectos de la pasta base?",
    answer:
      "Estimulación intensa y breve, euforia, irritabilidad, ansiedad, impulsividad, insomnio, pérdida de control y búsqueda repetida de consumo. CONASET clasifica la pasta base entre las drogas estimulantes que pueden generar euforia, desinhibición, agresividad, impulsividad, irritabilidad y disminución del sueño.",
  },
  {
    question: "¿Cuánta gente consume pasta base en Chile?",
    answer:
      "Según SENDA, el 0,3% de las personas entre 12 y 64 años declaró consumo de pasta base al menos una vez en el último año (15° Estudio en Población General), y el 1,8% de estudiantes de 8° básico a 4° medio lo declaró en el 15° Estudio en Población Escolar.",
  },
  {
    question: "¿La pasta base causa dependencia?",
    answer:
      "Sí. MINSAL informó que el 54,8% de consumidores de pasta base cumplía criterios de consumo problemático en datos 2016 del Décimo Segundo Estudio Nacional de Drogas en Población General.",
  },
  {
    question: "¿Qué hacer si mi hijo o hija consume pasta base?",
    answer:
      "Buscar orientación profesional temprana, evitar amenazas humillantes y activar redes de apoyo. MINSAL sugiere intervención motivacional, gestión de casos, intervención psicosocial y trabajo familiar en menores de 20 años con consumo perjudicial o dependencia.",
  },
  {
    question: "¿Dónde pedir ayuda en Chile?",
    answer:
      "En Chile puedes llamar al Fono Drogas y Alcohol 1412 de SENDA, gratuito, confidencial y disponible 24/7. También puedes acudir al CESFAM de tu comuna o consultar la red SinAdicciones.org.",
  },
];

export default function QueEsPastaBase() {
  const crumbs = [{ name: "¿Qué es la Pasta Base?", path: "/que-es-pasta-base" }];
  const schemas = [
    articleSchema({
      title: "Pasta Base en Chile: Qué es, Efectos, Riesgos y Estadísticas",
      description:
        "Qué es la pasta base, sus efectos físicos y psicológicos, cifras oficiales de SENDA y MINSAL, riesgos para adolescentes y opciones de ayuda en Chile.",
      path: "/que-es-pasta-base",
    }),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
    faqSchema(ARTICLE_FAQ),
  ];

  return (
    <>
      <Seo
        title="Pasta Base en Chile: Qué es, Efectos, Riesgos y Estadísticas | pasta-base.cl"
        description="Qué es la pasta base, sus efectos físicos y psicológicos, cifras oficiales de SENDA y MINSAL en Chile, riesgos para adolescentes y opciones de ayuda profesional."
        path="/que-es-pasta-base"
        type="article"
        schema={schemas}
      />
      <article className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            Información, efectos y estadísticas
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Pasta Base en Chile: Qué es, Efectos y Estadísticas
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-300">
            Una mirada clara y basada en evidencia: composición y origen,
            efectos físicos y psicológicos, cifras oficiales de SENDA y MINSAL,
            señales de alerta y rutas de ayuda en Chile.
          </p>
        </header>

        {/* Resumen ejecutivo */}
        <aside
          data-testid="resumen-ejecutivo"
          className="mt-10 rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-slate-900 p-6 sm:p-7"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">
                Resumen ejecutivo
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-200 sm:text-base">
                La pasta base de cocaína es una droga estimulante fumable, de
                efecto rápido y corta duración. SENDA la describe como un
                derivado intermedio de la cocaína compuesto por alcaloides de
                coca y solventes
                <Cite ids={["senda-pbc"]} />. En Chile, el 15° Estudio en
                Población General reportó que el{" "}
                <strong className="text-slate-50">0,3%</strong> de las personas
                entre 12 y 64 años declaró consumo en el último año, mientras
                que en población escolar (8° básico a 4° medio) la cifra
                alcanza el <strong className="text-slate-50">1,8%</strong>
                <Cite ids={["senda-pbc"]} />.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                Su prevalencia poblacional es menor que la del alcohol o la
                marihuana, pero su impacto sanitario es alto: entre consumidores,
                el <strong className="text-slate-50">54,8%</strong> cumplía
                criterios de consumo problemático según datos 2016 citados por
                MINSAL
                <Cite ids={["minsal-diprece"]} />.
              </p>
            </div>
          </div>
        </aside>

        <div className="prose-dark mt-10">
          <h2>Definición y composición química</h2>
          <p>
            La pasta base de cocaína (PBC) es un producto intermedio en la
            fabricación del clorhidrato de cocaína. Se obtiene tratando las
            hojas de coca con solventes orgánicos —kerosene, bencina, ácido
            sulfúrico, amoníaco y bicarbonato de sodio— en un proceso rústico y
            contaminado. El resultado es una sustancia de color crema o beige,
            altamente tóxica e impura, que se fuma en pipas artesanales
            mezclada con tabaco o marihuana.
          </p>
          <p>
            A diferencia de la cocaína en polvo, la pasta base contiene
            residuos químicos del proceso y adulterantes como cafeína,
            fenacetina, lidocaína, polvo de ladrillo o cal. Esta carga de
            impurezas explica gran parte de los daños específicos que produce:
            deterioro pulmonar, dental y dérmico.
          </p>

          <h2>Proceso de producción</h2>
          <p>
            El itinerario químico de la hoja de coca sigue tres etapas:
            maceración con solventes, extracción de alcaloides y precipitación.
            El producto resultante al final del segundo paso es precisamente la
            pasta base. Si se continúa el refinado con ácido clorhídrico y
            acetona, se obtiene el clorhidrato de cocaína (la cocaína “en
            polvo”). La pasta base, entonces, es un paso previo en la cadena de
            producción: más barata, más impura y más adictiva.
          </p>

          <h2>Diferencia con cocaína pura y crack</h2>
          <p>
            Aunque las tres sustancias provienen de la misma planta, son
            químicamente distintas en pureza, vía de consumo y efecto.
          </p>
          <div className="not-prose my-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Sustancia</th>
                  <th className="px-4 py-3">Pureza</th>
                  <th className="px-4 py-3">Forma</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                {COMP.map((r) => (
                  <tr key={r.drug}>
                    <td className="px-4 py-3 font-medium text-slate-100">{r.drug}</td>
                    <td className="px-4 py-3 text-slate-300">{r.purity}</td>
                    <td className="px-4 py-3 text-slate-400">{r.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2>Historia en Chile y Sudamérica</h2>
          <p>
            La pasta base llegó a Chile a mediados de la década de los 80s, en
            una primera ola asociada al consumo urbano marginal. Sin embargo,
            fue en los años 2000 cuando su consumo explotó, coincidiendo con
            cambios en las rutas de tráfico y con el empobrecimiento de
            sectores urbanos. Hoy, SENDA estima más de 50.000 consumidores
            problemáticos en el país, concentrados principalmente en la Región
            Metropolitana, Valparaíso, Biobío y Antofagasta. La droga también
            es dominante en barrios vulnerables de Argentina, Uruguay, Perú y
            Bolivia.
          </p>

          <h2>Nombres callejeros</h2>
          <p>
            En el lenguaje popular chileno y sudamericano, la pasta base tiene
            múltiples nombres según el contexto:
          </p>
          <div className="not-prose my-5 flex flex-wrap gap-2">
            {NAMES.map((n) => (
              <span
                key={n}
                className="rounded-full border border-slate-800 bg-slate-900/70 px-3 py-1.5 text-xs font-medium text-slate-300"
              >
                {n}
              </span>
            ))}
          </div>

          <h2>¿Por qué es tan adictiva? El cerebro bajo pasta base</h2>
          <p>
            La respuesta está en el cerebro. Al fumarse, la sustancia llega a
            los pulmones, de ahí pasa a la sangre y alcanza el cerebro en menos
            de 8 segundos. Allí bloquea la recaptación de dopamina —el
            neurotransmisor del placer— lo que produce una sobreexcitación
            masiva del circuito de recompensa. El resultado: una oleada de
            euforia, poder y bienestar que dura 10 a 15 minutos.
          </p>
          <p>
            Pero lo que la vuelve tan adictiva no es la euforia: es el “bajón”
            que viene después. Cuando la dopamina cae abruptamente, el cerebro
            queda en un estado de angustia, ansiedad e irritabilidad tan
            intenso que la única manera conocida de aliviarlo es volver a
            fumar. Así se instala el ciclo euforia-bajón-euforia, que en pocas
            semanas reorganiza el cerebro de la persona en torno a la droga.
          </p>

          {/* Infografía visual */}
          <div className="not-prose my-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-emerald-400">
              Cómo la pasta base afecta tu cerebro
            </h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                <Flame className="h-5 w-5 text-red-400" />
                <p className="mt-3 text-xs uppercase tracking-wider text-slate-500">
                  Segundo 1–8
                </p>
                <p className="mt-1 text-sm text-slate-200">
                  La droga alcanza el cerebro. Sobreexcitación dopaminérgica.
                </p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                <Pill className="h-5 w-5 text-emerald-300" />
                <p className="mt-3 text-xs uppercase tracking-wider text-slate-500">
                  Minutos 1–15
                </p>
                <p className="mt-1 text-sm text-slate-200">
                  Euforia intensa. Dilatación pupilar. Sensación de
                  invulnerabilidad.
                </p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                <AlertTriangle className="h-5 w-5 text-amber-300" />
                <p className="mt-3 text-xs uppercase tracking-wider text-slate-500">
                  Minutos 15–45
                </p>
                <p className="mt-1 text-sm text-slate-200">
                  Caída abrupta. Angustia. Necesidad urgente de volver a fumar.
                </p>
              </div>
            </div>
          </div>

          <h2>Por qué la pasta base produce efectos tan intensos</h2>
          <p>
            La pasta base se fuma, por lo que la sustancia entra rápidamente al
            organismo a través de los pulmones. SENDA indica que su consumo
            fumado genera efectos estimulantes inmediatos y de corta duración,
            lo que explica que el patrón de uso pueda volverse compulsivo cuando
            la persona busca repetir el efecto inicial
            <Cite ids={["senda-pbc"]} />.
          </p>
          <p>
            CONASET clasifica la cocaína y la pasta base entre las drogas
            estimulantes, advirtiendo que pueden generar euforia, desinhibición,
            agresividad, falta de sensación de cansancio, disminución del sueño,
            impulsividad e irritabilidad
            <Cite ids={["conaset"]} />. Tras el efecto inicial pueden aparecer
            angustia, ansiedad, ideas paranoides, búsqueda urgente de más droga
            y abandono progresivo de actividades habituales.
          </p>

          <h2>Efectos de la pasta base a corto plazo</h2>
          <p>
            Los efectos varían según la persona, la frecuencia de consumo, la
            cantidad usada, la presencia de otras sustancias y el estado físico
            o psicológico previo. Estas son las áreas que más se afectan:
          </p>
          <div className="not-prose my-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Área afectada</th>
                  <th className="px-4 py-3">Efectos posibles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                {EFECTOS_CORTO.map((row) => (
                  <tr key={row.area}>
                    <td className="px-4 py-3 font-medium text-slate-100 align-top sm:w-48">
                      {row.area}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{row.efectos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Estos efectos deben leerse como señales de alerta, no como una lista
            de diagnóstico. Si hay consumo frecuente, pérdida de control,
            abstinencia, abandono de responsabilidades o persistencia pese al
            daño, corresponde buscar evaluación profesional.
          </p>

          <h2>Efectos a largo plazo y daño acumulativo</h2>
          <p>
            MINSAL define el <strong>consumo perjudicial</strong> como una forma
            de consumo que ya afecta la salud física o mental, y describe la
            <strong> dependencia</strong> como un cuadro en que la sustancia
            pasa a ocupar una prioridad central en la vida de la persona, con
            deseo intenso, dificultad de control, tolerancia, abstinencia y
            persistencia pese a consecuencias perjudiciales
            <Cite ids={["minsal-diprece"]} />.
          </p>
          <p>
            En la práctica, el consumo sostenido suele expresarse como deterioro
            del sueño, pérdida de peso, descuido de la higiene, conflictos
            familiares, aislamiento, endeudamiento, exposición a violencia,
            deserción escolar o laboral y aumento de síntomas ansiosos,
            depresivos o paranoides. En adolescentes, MINSAL destaca que los
            trastornos por consumo requieren diagnóstico, intervenciones breves
            y referencia asistida cuando corresponda
            <Cite ids={["minsal-diprece"]} />.
          </p>

          <h2 id="estadisticas">Estadísticas de pasta base en Chile</h2>
          <p>
            Las cifras chilenas deben interpretarse con cuidado: una prevalencia
            baja en población general no significa bajo daño sanitario. Significa
            que una proporción pequeña declara consumo, pero dentro de ese grupo
            el riesgo de dependencia y deterioro puede ser elevado.
          </p>

          {/* Tabla de estadísticas con fuentes */}
          <div className="not-prose my-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Indicador</th>
                  <th className="px-4 py-3 text-right sm:text-left">Dato</th>
                  <th className="hidden px-4 py-3 sm:table-cell">Lectura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                {STATS_OFICIALES.map((r) => (
                  <tr key={r.indicador}>
                    <td className="px-4 py-3 font-medium text-slate-100">
                      {r.indicador}
                      <Cite ids={r.sources} />
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-300 font-bold sm:text-left">
                      {r.valor}
                    </td>
                    <td className="hidden px-4 py-3 text-xs text-slate-400 sm:table-cell">
                      {r.lectura}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p>
            El 15° Estudio Nacional de Drogas en Población General fue aplicado
            entre noviembre de 2022 y mayo de 2023, con una muestra de 17.454
            personas de 12 a 65 años, representando a 12.941.545 habitantes de
            109 comunas
            <Cite ids={["senda-resultados"]} />. SENDA realiza estos estudios
            nacionales sobre población general y escolar de manera alternada,
            con frecuencia bienal, desde 1994, a través del Observatorio Chileno
            de Drogas
            <Cite ids={["senda-observatorio"]} />.
          </p>

          {/* Bloque escolares */}
          <div className="not-prose my-8 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6">
            <div className="flex items-start gap-3">
              <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <div>
                <h3 className="text-base font-bold text-amber-200">
                  Pasta base en escolares: por qué el dato importa
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-200">
                  El 1,8% en estudiantes de 8° básico a 4° medio puede parecer
                  bajo, pero en prevención no debe minimizarse. Se trata de una
                  etapa en que el cerebro, la identidad y la regulación
                  emocional siguen en desarrollo. SENDA señala que el 15°
                  Estudio Escolar 2023 es una herramienta clave para orientar
                  políticas de prevención en niños, niñas y adolescentes
                  <Cite ids={["senda-escolar"]} />.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  MINSAL recomienda en menores de 20 años con consumo perjudicial
                  o dependencia realizar intervención motivacional de enganche,
                  gestión de casos, intervenciones psicosociales individuales y
                  grupales, y trabajo familiar cuando sea pertinente
                  <Cite ids={["minsal-recomendaciones"]} />.
                </p>
              </div>
            </div>
          </div>

          <h2>Cómo interpretar las incautaciones de pasta base</h2>
          <p>
            Las incautaciones permiten observar actividad policial, rutas de
            tráfico y presión del mercado ilegal, pero no equivalen directamente
            a consumo. En 2024, Aduanas reportó procedimientos en pasos
            fronterizos donde se incautaron pasta base, cocaína, ketamina y
            cannabis, incluyendo un caso en Chacalluta con{" "}
            <strong>4 kg 290 g</strong> de pasta base ocultos en paquetes
            adheridos al cuerpo de una pasajera
            <Cite ids={["aduanas-chacalluta"]} />.
          </p>
          <p>
            En agosto del mismo año, Aduanas informó otro procedimiento en la
            avanzada El Loa donde se hallaron <strong>9 kg 694 g</strong> de
            pasta base adheridos con imanes bajo asientos de un bus
            <Cite ids={["aduanas-elloa"]} />. Estos casos muestran que la pasta
            base forma parte de circuitos de tráfico que usan fronteras, buses
            y vehículos diversos, pero no permiten estimar por sí solos cuántas
            personas consumen.
          </p>

          <h2 id="senales">Señales de alerta en una persona que podría estar consumiendo</h2>
          <p>
            Las señales pueden aparecer de manera gradual o abrupta. No todas
            prueban consumo por sí solas, pero cuando se acumulan conviene
            conversar, observar y pedir orientación profesional.
          </p>
          <div className="not-prose my-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Tipo de señal</th>
                  <th className="px-4 py-3">Ejemplos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                {SENALES.map((row) => (
                  <tr key={row.tipo}>
                    <td className="px-4 py-3 font-medium text-slate-100 align-top sm:w-56">
                      <span className="inline-flex items-start gap-2">
                        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        {row.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{row.ejemplos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2>Qué hacer si un familiar consume pasta base</h2>
          <p>
            Lo primero es evitar la confrontación humillante. El consumo
            problemático suele estar acompañado de culpa, negación, miedo, deuda
            o síntomas de salud mental, por lo que una conversación agresiva
            puede cerrar la posibilidad de ayuda.
          </p>
          <p>
            Una forma más efectiva de iniciar el diálogo es hablar desde la
            preocupación concreta: <em>“he notado que no duermes, que faltas al
            trabajo y que estás muy angustiado; me preocupa tu salud y quiero
            ayudarte a buscar orientación”</em>. Esta estrategia no reemplaza
            tratamiento, pero abre la puerta a una evaluación profesional.
          </p>
          <p>
            SENDA informa que el <strong>Fono Drogas y Alcohol 1412</strong> es
            gratuito, confidencial y disponible las 24 horas, y atiende más de
            22 mil llamadas anuales
            <Cite ids={["senda-1412"]} />. SENDA también sumó un canal de chat
            en senda.gob.cl disponible de lunes a domingo entre 08:00 y 20:00
            horas, orientado a facilitar el acceso de públicos más jóvenes
            <Cite ids={["senda-1412"]} />.
          </p>

          <h2>Tratamiento para consumo de pasta base</h2>
          <p>
            El tratamiento debe adaptarse a la edad, gravedad del consumo, salud
            mental, red familiar, riesgo social y presencia de otras sustancias.
            MINSAL recomienda en menores de 20 años intervención motivacional,
            gestión de casos, intervención psicosocial individual y grupal, y
            trabajo familiar cuando sea clínicamente adecuado
            <Cite ids={["minsal-recomendaciones"]} />.
          </p>
          <p>
            En el caso de cocaínas fumables con craving, MINSAL sugiere
            intervención psicosocial sin tratamiento farmacológico como primera
            comparación general, aunque reconoce que algunos pacientes podrían
            requerir evaluación médica personalizada cuando existen síntomas
            concomitantes más severos
            <Cite ids={["minsal-recomendaciones"]} />. No existe una “pastilla
            única” que resuelva la dependencia: el núcleo del tratamiento suele
            ser psicosocial, familiar y comunitario.
          </p>
          <p>
            Para conocer modalidades, etapas y costos en Chile, revisa la guía
            completa de <Link to="/tratamientos">tratamientos</Link>.
          </p>

          <h2>Prevención: qué ayuda a reducir el riesgo</h2>
          <p>
            La prevención no se limita a informar que “hace daño”. Funciona
            mejor cuando se combina con habilidades parentales, apoyo escolar,
            detección temprana, vínculos significativos y acceso rápido a
            ayuda. SENDA describe programas de parentalidad orientados a
            prevenir el consumo mediante mayor involucramiento de adultos
            cuidadores y fortalecimiento de habilidades para una parentalidad
            positiva
            <Cite ids={["senda-parentalidad"]} />.
          </p>

          <h2 id="mitos">Mitos frecuentes sobre la pasta base</h2>
          <div className="not-prose my-6 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Mito</th>
                  <th className="px-4 py-3">Realidad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/40 text-slate-200">
                {MITOS.map((row) => (
                  <tr key={row.mito}>
                    <td className="px-4 py-3 align-top text-slate-300 sm:w-1/3">
                      <span className="inline-flex items-start gap-2">
                        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                        {row.mito}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-200">{row.realidad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 id="faq">Preguntas frecuentes sobre la pasta base</h2>
          <div className="not-prose my-6 divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            {ARTICLE_FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`que-es-faq-${i}`}
                className="group px-5 py-4"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-left text-base font-semibold text-slate-100 marker:hidden">
                  <span className="flex items-start gap-3">
                    <span className="mt-0.5 text-xs font-bold text-emerald-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {f.question}
                  </span>
                  <span className="shrink-0 rounded-full bg-slate-800 p-1.5 text-slate-400 transition group-open:rotate-45 group-open:bg-emerald-500/10 group-open:text-emerald-300">
                    <ArrowRight className="h-4 w-4 -rotate-45 transition" />
                  </span>
                </summary>
                <p className="mt-3 pl-7 text-sm leading-relaxed text-slate-400">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>

          <h2 id="fuentes">Fuentes oficiales</h2>
          <ol>
            {Object.values(SOURCES).map((s) => (
              <li key={s.href}>
                <a href={s.href} target="_blank" rel="noopener noreferrer">
                  {s.label}
                </a>
              </li>
            ))}
          </ol>

          <h2>Si tú o alguien cercano consume</h2>
          <p>
            La pasta base es una droga tratable. Miles de personas en Chile
            dejan el consumo todos los años con apoyo profesional. La
            recuperación es posible. Si estás preocupado por ti mismo o por un
            ser querido:
          </p>
          <ul>
            <li>
              Llama al <a href="tel:1412">Fono Drogas SENDA 1412</a> (24/7,
              gratuito).
            </li>
            <li>
              Acude al CESFAM de tu comuna para iniciar una derivación gratuita.
            </li>
            <li>
              Revisa la red{" "}
              <a
                href="https://sinadicciones.org"
                target="_blank"
                rel="noopener noreferrer"
              >
                SinAdicciones.org
              </a>{" "}
              para encontrar centros y profesionales verificados.
            </li>
            <li>
              Haz nuestro{" "}
              <Link to="/test-dependencia">test de dependencia</Link> para
              obtener una orientación inmediata.
            </li>
          </ul>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6">
          <p className="flex-1 text-sm text-slate-200">
            <strong className="font-semibold text-emerald-300">¿Consumes o conoces a alguien que consume?</strong>{" "}
            Busca ayuda hoy. Es gratis, confidencial y efectiva.
          </p>
          <a
            href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
          >
            Buscar centros
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </article>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="que-es" />
        </div>
      </section>
    </>
  );
}
